const ASPECT_RATIO_PATTERN = /^\d+(\.\d+)?:\d+(\.\d+)?$/;
const PRIMARY_CATEGORIES = [
  "commercial",
  "feature-film",
  "tv-series",
  "music-video",
];
const EXTRA_CATEGORIES = ["featured", "showreel"];

const DIACRITIC_MAP = {
  æ: "ae",
  Æ: "ae",
  ø: "o",
  Ø: "o",
  å: "a",
  Å: "a",
  ü: "u",
  Ü: "u",
  ö: "o",
  Ö: "o",
  ä: "a",
  Ä: "a",
};

function yamlSingleQuote(value) {
  return `'${String(value).replace(/'/g, "''")}'`;
}

function yamlFlowScalar(value) {
  const text = String(value).trim();
  if (
    !text ||
    /[:#,\n{}'"]/.test(text) ||
    text.includes("[") ||
    text.includes("]")
  ) {
    return yamlSingleQuote(text);
  }
  return text;
}

function foldDiacritics(value) {
  return String(value)
    .replace(/[æÆøØåÅüÜöÖäÄ]/g, (char) => DIACRITIC_MAP[char] || char)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function slugifyPart(value) {
  return foldDiacritics(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function suggestWorkSlug({ date, clients = [], title }) {
  const day = (date || "").trim() || "2026-01-01";
  const clientPart = clients
    .map((client) => slugifyPart(client?.name))
    .filter(Boolean)
    .join("-");
  const titlePart = slugifyPart(title);
  return [day, clientPart, titlePart].filter(Boolean).join("-");
}

export function isValidAspect(value) {
  return typeof value === "string" && ASPECT_RATIO_PATTERN.test(value.trim());
}

export function extractImdbId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  const fromUrl = trimmed.match(/imdb\.com\/title\/(tt\d+)/i);
  if (fromUrl) return fromUrl[1];
  const fromId = trimmed.match(/^(tt\d+)$/i);
  return fromId ? fromId[1] : null;
}

export function normalizeImdbLink(url) {
  const id = extractImdbId(url);
  if (!id) {
    const trimmed = typeof url === "string" ? url.trim() : "";
    return trimmed || "";
  }
  return `https://www.imdb.com/title/${id}/`;
}

export function formatWorkDate(date) {
  const trimmed = (date || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00.000Z`;
  }
  return trimmed;
}

export function buildWorkCategories({ primary, featured, showreel }) {
  const categories = [];
  if (PRIMARY_CATEGORIES.includes(primary)) {
    categories.push(primary);
  }
  if (featured) categories.push("featured");
  if (showreel) categories.push("showreel");
  return categories;
}

function namedClients(clients) {
  return (clients || [])
    .map((client) => ({
      name: (client?.name || "").trim(),
      url: (client?.url || "").trim(),
    }))
    .filter((client) => client.name);
}

function headingMarkdownLevel(level) {
  return Number(level) >= 3 ? 3 : 2;
}

function textNode(text, marks = []) {
  if (!text) return null;
  const node = { type: "text", text };
  if (marks.length > 0) node.marks = marks;
  return node;
}

function parseInlineMarkdown(line, inheritedMarks = []) {
  const nodes = [];
  const pattern =
    /\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*|_(.+?)_|\*([^*]+)\*/g;
  let lastIndex = 0;
  let match = pattern.exec(line);
  while (match) {
    if (match.index > lastIndex) {
      const plain = textNode(
        line.slice(lastIndex, match.index),
        inheritedMarks,
      );
      if (plain) nodes.push(plain);
    }
    if (match[1] != null) {
      nodes.push(
        ...parseInlineMarkdown(match[1], [
          ...inheritedMarks,
          { type: "link", attrs: { href: match[2] } },
        ]),
      );
    } else if (match[3] != null) {
      nodes.push(
        ...parseInlineMarkdown(match[3], [
          ...inheritedMarks,
          { type: "bold" },
        ]),
      );
    } else {
      const italicText = match[4] != null ? match[4] : match[5];
      nodes.push(
        ...parseInlineMarkdown(italicText, [
          ...inheritedMarks,
          { type: "italic" },
        ]),
      );
    }
    lastIndex = match.index + match[0].length;
    match = pattern.exec(line);
  }
  if (lastIndex < line.length) {
    const plain = textNode(line.slice(lastIndex), inheritedMarks);
    if (plain) nodes.push(plain);
  }
  return nodes;
}

export function serializeEditorDoc(doc) {
  if (!doc) return "";

  const serializeMarks = (text, marks = []) => {
    let out = text;
    const types = new Set((marks || []).map((mark) => mark.type));
    if (types.has("italic")) out = `_${out}_`;
    if (types.has("bold")) out = `**${out}**`;
    const link = (marks || []).find((mark) => mark.type === "link");
    if (link?.attrs?.href) {
      return `[${out}](${link.attrs.href})`;
    }
    return out;
  };

  const serializeNode = (node) => {
    if (!node) return "";
    if (node.type === "text") {
      return serializeMarks(node.text || "", node.marks || []);
    }
    if (node.type === "hardBreak") return "\n";
    const inner = (node.content || []).map(serializeNode).join("");
    if (node.type === "heading") {
      const level = headingMarkdownLevel(node.attrs?.level || 2);
      return `${"#".repeat(level)} ${inner}`.trimEnd();
    }
    if (node.type === "paragraph") return inner;
    if (node.type === "doc") {
      return (node.content || [])
        .map(serializeNode)
        .join("\n\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }
    return inner;
  };

  return serializeNode(doc);
}

export function buildWorkMarkdown(input = {}) {
  const title = (input.title || "").trim();
  const clients = namedClients(input.clients);
  const date = formatWorkDate(input.date);
  const imdbLink = normalizeImdbLink(input.imdbLink);
  const imdbRating =
    typeof input.imdbRating === "number" &&
    Number.isFinite(input.imdbRating) &&
    input.imdbRating >= 1 &&
    input.imdbRating <= 10
      ? input.imdbRating
      : null;
  const aspect = isValidAspect(input.aspect) ? input.aspect.trim() : "";
  const categories = Array.isArray(input.categories)
    ? input.categories.filter(Boolean)
    : buildWorkCategories(input);
  const service = (input.service || "").trim();
  const equipment = (input.equipment || []).filter(Boolean);
  const muxPlaybackId = (input.muxPlaybackId || "").trim();
  const muxAssetId = (input.muxAssetId || "").trim();
  const contentEn = (input.contentEn || "").trim();
  const contentNo = (input.contentNo || "").trim();

  const lines = ["---"];
  lines.push(`title: ${yamlSingleQuote(title)}`);

  if (clients.length > 0) {
    lines.push(
      `client: [${clients.map((client) => yamlFlowScalar(client.name)).join(", ")}]`,
    );
    const hasAnyLink = clients.some((client) => client.url);
    if (hasAnyLink) {
      lines.push("client_links:");
      clients.forEach((client) => {
        lines.push(`  - "${client.url}"`);
      });
    }
  }

  if (date) {
    lines.push(`date: ${date}`);
  }

  if (imdbLink) {
    lines.push(`imdb_link: ${yamlSingleQuote(imdbLink)}`);
    if (imdbRating != null) {
      lines.push(`imdb_rating: ${imdbRating}`);
    }
  }

  if (aspect) {
    lines.push(`aspect: ${yamlSingleQuote(aspect)}`);
  }

  if (categories.length > 0) {
    lines.push("categories:");
    categories.forEach((category) => {
      lines.push(`  - ${category}`);
    });
  }

  if (service) {
    lines.push(`service: ${yamlSingleQuote(service)}`);
  }

  if (equipment.length > 0) {
    lines.push("equipment:");
    equipment.forEach((item) => {
      lines.push(`  - ${item}`);
    });
  }

  lines.push(`mux_playback_id: ${muxPlaybackId}`);
  lines.push(`mux_asset_id: ${muxAssetId}`);
  lines.push("---");

  let body = "";
  if (contentEn) {
    body += `\n\n${contentEn}\n`;
  }
  if (contentNo) {
    if (!contentEn) body += "\n";
    body += `\n<!-- locale:no -->\n${contentNo}\n`;
  } else if (contentEn) {
    body += "\n";
  }

  return `${lines.join("\n")}${body}`;
}

function unquoteYaml(value) {
  const text = String(value || "").trim();
  if (
    (text.startsWith("'") && text.endsWith("'")) ||
    (text.startsWith('"') && text.endsWith('"'))
  ) {
    return text.slice(1, -1).replace(/''/g, "'");
  }
  return text;
}

function parseFlowClientList(value) {
  const trimmed = unquoteYaml(value);
  const inner = trimmed.startsWith("[") && trimmed.endsWith("]")
    ? trimmed.slice(1, -1)
    : trimmed;
  if (!inner) return [];
  return inner
    .split(",")
    .map((part) => unquoteYaml(part))
    .filter(Boolean);
}

function parseYamlList(lines, startIndex) {
  const items = [];
  let index = startIndex + 1;
  while (index < lines.length && /^\s+-\s*/.test(lines[index])) {
    items.push(unquoteYaml(lines[index].replace(/^\s+-\s*/, "")));
    index += 1;
  }
  return { items, nextIndex: index };
}

export function parseWorkMarkdown(raw = "", slug = "") {
  const source = String(raw || "");
  let frontmatter = "";
  let body = source;
  if (source.startsWith("---")) {
    const end = source.indexOf("\n---", 3);
    if (end >= 0) {
      frontmatter = source.slice(3, end).trim();
      body = source.slice(end + 4);
    }
  }

  const data = {};
  const lines = frontmatter.split("\n");
  for (let index = 0; index < lines.length; ) {
    const line = lines[index];
    const match = line.match(/^([A-Za-z0-9_]+):\s*(.*)$/);
    if (!match) {
      index += 1;
      continue;
    }
    const key = match[1];
    const value = match[2];
    if (value === "" && index + 1 < lines.length && /^\s+-/.test(lines[index + 1])) {
      const list = parseYamlList(lines, index);
      data[key] = list.items;
      index = list.nextIndex;
      continue;
    }
    data[key] = unquoteYaml(value);
    index += 1;
  }

  const localeMatch = body.match(/<!--\s*locale:no\s*-->/i);
  const contentEn = localeMatch
    ? body.slice(0, localeMatch.index).trim()
    : body.trim();
  const contentNo = localeMatch
    ? body.slice(localeMatch.index + localeMatch[0].length).trim()
    : "";

  const categories = Array.isArray(data.categories)
    ? data.categories
    : data.categories
      ? [data.categories]
      : [];
  const primary =
    categories.find((category) => PRIMARY_CATEGORIES.includes(category)) ||
    "commercial";
  const names = parseFlowClientList(data.client || "");
  const urls = Array.isArray(data.client_links) ? data.client_links : [];
  const clients = names.length
    ? names.map((name, index) => ({ name, url: urls[index] || "" }))
    : [{ name: "", url: "" }];

  const ratingRaw = data.imdb_rating;
  const parsedRating = Number.parseFloat(ratingRaw);
  const imdbRating =
    Number.isFinite(parsedRating) && parsedRating >= 1 && parsedRating <= 10
      ? parsedRating
      : null;

  const dateRaw = String(data.date || "");
  const dateMatch = dateRaw.match(/^(\d{4}-\d{2}-\d{2})/);

  return {
    slug,
    title: data.title || "",
    date: dateMatch ? dateMatch[1] : "",
    clients,
    imdbLink: data.imdb_link || "",
    imdbRating,
    aspect: data.aspect || "",
    primary,
    featured: categories.includes("featured"),
    showreel: categories.includes("showreel"),
    service: data.service || "",
    equipment: Array.isArray(data.equipment) ? data.equipment : [],
    muxPlaybackId: data.mux_playback_id || "",
    muxAssetId: data.mux_asset_id || "",
    contentEn,
    contentNo,
  };
}

export function markdownToEditorContent(markdown) {
  const text = (markdown || "").trim();
  if (!text) {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }

  const content = [];
  const paragraphLines = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const joined = paragraphLines.join(" ").replace(/ +/g, " ").trim();
    const inline = parseInlineMarkdown(joined);
    content.push({
      type: "paragraph",
      content: inline.length ? inline : undefined,
    });
    paragraphLines.length = 0;
  };

  text.split("\n").forEach((line) => {
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      const inline = parseInlineMarkdown(heading[2]);
      content.push({
        type: "heading",
        attrs: { level: headingMarkdownLevel(heading[1].length) },
        content: inline.length ? inline : undefined,
      });
      return;
    }
    if (!line.trim()) {
      flushParagraph();
      return;
    }
    paragraphLines.push(line.trim());
  });
  flushParagraph();

  return {
    type: "doc",
    content: content.length ? content : [{ type: "paragraph" }],
  };
}

export const DEFAULT_WORK_BODY_EN = [
  "For this production, Motion Air provided the [Radical Arm](/services/pursuit/radical) for tracking shots. Use Link equipment to point at the kit you actually used.",
  "",
  "## Field Rigging and Logistics",
  "Describe how you packed, transported, and got camera-ready on site.",
  "",
  "## Efficient Turnaround",
  "Describe wrap, pack-down, and how the shoot stayed on schedule.",
].join("\n");

export const DEFAULT_WORK_BODY_NO = [
  "På denne produksjonen leverte Motion Air [Radical Arm](/services/pursuit/radical) til tracking-opptak. Bruk Link equipment for å peke på utstyret dere faktisk brukte.",
  "",
  "## Rigging og logistikk i felt",
  "Beskriv pakking, transport og hvordan dere ble klare til opptak på stedet.",
  "",
  "## Effektiv gjennomføring",
  "Beskriv nedrigging og hvordan opptaket ble holdt på tidsplanen.",
].join("\n");

function normalizeBody(value) {
  return String(value || "").trim();
}

export function isDefaultWorkBody(contentEn, contentNo) {
  return (
    normalizeBody(contentEn) === DEFAULT_WORK_BODY_EN &&
    normalizeBody(contentNo) === DEFAULT_WORK_BODY_NO
  );
}

export function isMeaningfulWorkForm(fields = {}) {
  if ((fields.muxPlaybackId || "").trim() || (fields.muxAssetId || "").trim()) {
    return true;
  }
  if ((fields.title || "").trim()) return true;
  if ((fields.imdbLink || "").trim()) return true;
  if ((fields.equipmentIds || []).length > 0) return true;
  if ((fields.clients || []).some((client) => (client?.name || "").trim())) {
    return true;
  }
  if ((fields.service || "").trim()) return true;
  if (!isDefaultWorkBody(fields.contentEn, fields.contentNo)) {
    if (normalizeBody(fields.contentEn) || normalizeBody(fields.contentNo)) {
      return true;
    }
  }
  return false;
}

export { PRIMARY_CATEGORIES, EXTRA_CATEGORIES, ASPECT_RATIO_PATTERN };
